import React from 'react';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
} from 'recharts';

/**
 * "Statut des commandes" — donut chart.
 *
 * Props:
 *  - data:    { total, segments: [{status, label, color, count, percentage}] }
 *  - loading, error
 *
 * Segments are pre-ordered and pre-colored by the backend (config.SegmentOrder
 * in server.py). "En attente" is a synthetic segment = call_1_message_1 +
 * call_2_message_2 + call_3 (client decision — no breakdown in the donut nor
 * in the legend).
 */
function DonutTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-lg px-3 py-2 text-xs">
            <div className="font-semibold" style={{ color: p.color }}>{p.label}</div>
            <div className="text-[#6B7280] mt-0.5">
                {Number(p.count).toLocaleString('fr-FR')} · {p.percentage}%
            </div>
        </div>
    );
}

function fmtPct(n) {
    return `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}%`;
}

export default function StatusDonutChart({ data, loading, error, onSegmentClick, onReportClick }) {
    const segments = (data?.segments || []).filter((s) => s.count > 0);
    const total = data?.total ?? 0;
    const clickable = typeof onSegmentClick === 'function';

    return (
        <div data-testid="status-donut-card" className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] p-4 flex flex-col h-full">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-semibold text-[#1A1D2E]">Statut des commandes</h3>
                <span className="text-xs text-[#6B7280]">Période courante</span>
            </div>

            {loading && (
                <div data-testid="status-donut-loading" className="flex-1 flex items-center justify-center min-h-[200px] p-2">
                    <div className="w-[190px] h-[190px] rounded-full bg-[#F5F6FA] overflow-hidden relative">
                        <span className="absolute inset-0 block animate-shimmer bg-gradient-to-r from-transparent via-white to-transparent" />
                    </div>
                </div>
            )}
            {!loading && error && (
                <div data-testid="status-donut-error" className="flex-1 flex items-center justify-center text-sm text-[#EF4444] min-h-[200px]">
                    {typeof error === 'string' ? error : 'Erreur'}
                </div>
            )}
            {!loading && !error && total === 0 && (
                <div data-testid="status-donut-empty" className="flex-1 flex flex-col items-center justify-center text-sm text-[#6B7280] min-h-[200px] gap-1">
                    <div className="text-[#1A1D2E] font-semibold text-[13px]">Aucune donnée pour cette période</div>
                    <div className="text-xs">Aucune commande n'a été enregistrée sur cette plage.</div>
                </div>
            )}

            {!loading && !error && total > 0 && (
                <div className="flex-1 flex flex-col md:flex-row items-center gap-4">
                    {/* Donut */}
                    <div className="relative w-[190px] h-[190px] shrink-0" data-testid="status-donut-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={segments}
                                    dataKey="count"
                                    nameKey="label"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={62}
                                    outerRadius={88}
                                    paddingAngle={2}
                                    strokeWidth={0}
                                    isAnimationActive={false}
                                    onClick={clickable ? (entry) => onSegmentClick(entry) : undefined}
                                    style={clickable ? { cursor: 'pointer' } : undefined}
                                >
                                    {segments.map((s) => (
                                        <Cell
                                            key={s.status}
                                            fill={s.color}
                                            data-testid={`status-donut-slice-${s.status}`}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<DonutTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <div className="text-2xl font-bold text-[#1A1D2E]" data-testid="status-donut-total">
                                {Number(total).toLocaleString('fr-FR')}
                            </div>
                            <div className="text-[11px] text-[#6B7280]">commandes</div>
                        </div>
                    </div>

                    {/* Legend — items are also clickable when onSegmentClick is provided */}
                    <ul className="flex-1 space-y-2 w-full" data-testid="status-donut-legend">
                        {(data?.segments || []).map((s) => {
                            const rowClass =
                                'flex items-center gap-2 text-sm rounded-md px-1 py-1 ' +
                                (clickable && s.count > 0
                                    ? 'cursor-pointer hover:bg-[#F5F6FA] transition-colors'
                                    : '');
                            const rowProps = clickable && s.count > 0
                                ? { onClick: () => onSegmentClick(s), role: 'button', tabIndex: 0,
                                    onKeyDown: (e) => (e.key === 'Enter' || e.key === ' ') && onSegmentClick(s) }
                                : {};
                            return (
                                <li
                                    key={s.status}
                                    data-testid={`status-legend-${s.status}`}
                                    className={rowClass}
                                    {...rowProps}
                                >
                                    <span
                                        className="w-2.5 h-2.5 rounded-sm shrink-0"
                                        style={{ backgroundColor: s.color }}
                                    />
                                    <span className="flex-1 text-[#1A1D2E] truncate">{s.label}</span>
                                    <span className="text-[#6B7280] tabular-nums" title={`${s.count} commandes`}>
                                        {Number(s.count).toLocaleString('fr-FR')}
                                    </span>
                                    <span className="text-[#6B7280] font-medium w-10 text-right tabular-nums">
                                        {fmtPct(s.percentage)}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* Full-period drill-down link → /commandes with the current
                dashboard period pre-applied (no status filter). */}
            {typeof onReportClick === 'function' ? (
                <button
                    type="button"
                    data-testid="status-donut-report-link"
                    onClick={onReportClick}
                    className="mt-3 pt-3 border-t border-[#E5E7EB] text-sm text-[#4B3FD8] font-medium hover:underline text-left w-full"
                >
                    Voir le rapport →
                </button>
            ) : (
                <div
                    data-testid="status-donut-report-link"
                    className="mt-3 pt-3 border-t border-[#E5E7EB] text-sm text-[#4B3FD8] font-medium cursor-not-allowed opacity-70 select-none"
                    aria-disabled="true"
                >
                    Voir le rapport →
                </div>
            )}
        </div>
    );
}
